import { forwardRef, type HTMLAttributes } from 'react';
import { AlarmClock, Check, ListChecks, ShieldAlert, Sparkles } from 'lucide-react';
import type { TaskCard, TaskLabel, TaskMember, TaskSubtaskTitle, TaskType } from '@shared/types/tasks';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '@/components/StatusBadge';
import { isSnoozed, snoozeDay } from '@/lib/snooze';
import { CONTEXT_ICONS } from './task-ui';
import { useFileDrop } from '@/components/useFileDrop';
import { useFormatTaskRef } from '@/lib/current-project';
import { formatTaskNumber } from '@shared/task-number';
import { ProjectIcon } from '@/components/factory/ProjectIcon';
import { useTaskCardProject } from './task-card-project';
import { TaskCardPrs } from './TaskCardPrs';
import { TaskCardPatchButton } from './TaskPatchRequests';
import { taskSummaryText } from './TaskAiSummary';
import { StackedOn } from './StackedOn';
import { SubtaskAfter, SubtaskOf } from './SubtaskOf';
import { TaskCardChips, TaskCardCounts, TaskCardPeople, TaskRequestor } from './TaskCardMeta';
import { PriorityIcon } from './TaskPriority';
import { UpstreamMark } from './task-chips';
import { BrokenLinkBadge } from './BrokenLink';
import { hasTaskCardCounts, hasTaskCardPeople, taskCardFields, useTaskTurn } from './task-card-meta';
import { FailedRunBadge, PickupSkipBadge, RunLimitBadge } from './PickupSkip';
import { pickupSkipOf } from './task-waiting';
import { RunProgressBar, SubtaskRunsProgressStripe, subtaskRunsSentence } from '@/components/factory/RunProgressBar';
import { useSubtaskActiveRuns, useTaskActiveRun } from '@/components/factory/active-runs';
import { isRunActive, RUN_PICKS_META } from '@/components/factory/run-status';

interface TaskCardViewProps extends HTMLAttributes<HTMLDivElement> {
  task: TaskCard;
  members: TaskMember[];
  labels: TaskLabel[];
  /** The Board's types: a card that isn't the default type shows its type. */
  types?: TaskType[];
  /** Leave the type chip out (the Board filtered to one type, so every card would say it). */
  hideType?: boolean;
  /** Fade it when it's waiting on someone else (the My tasks view). */
  fadeWhenWaiting?: boolean;
  /** Hide the Context chips (e.g. in a Tasks panel where they're all the same page). */
  hideContexts?: boolean;
  /** Show the AI summary inside the card, under the title (the Board's Summaries switch, T-17). */
  showSummary?: boolean;
  dragging?: boolean;
  /** While picking cards on the Board: whether this one is picked (undefined when not picking). */
  selected?: boolean;
  /** Files dropped on the card from the computer are added to the task. */
  onDropFiles?: (files: File[]) => void;
  /** SF-T-176: its sub-tasks the Board's search matched, named on the card. */
  matchedSubtasks?: TaskSubtaskTitle[];
}

/** A task on the Board or in a Tasks panel. */
export const TaskCardView = forwardRef<HTMLDivElement, TaskCardViewProps>(function TaskCardView(
  { task, members, labels, types, hideType, fadeWhenWaiting, hideContexts, showSummary, dragging, selected, onDropFiles, matchedSubtasks, className, style, ...props },
  ref
) {
  const drop = useFileDrop(onDropFiles);
  const formatCurrentRef = useFormatTaskRef();
  // On the master board (T-26) each card says whose it is: its project's icon, prefix and colour.
  const project = useTaskCardProject(task.id);
  const formatRef = (n: number) => (project ? formatTaskNumber(n, project.task_prefix) : formatCurrentRef(n));
  // Its pull requests, live from GitHub, with Merge (T-3).
  const { prs, priority } = taskCardFields(task, types, hideType);
  const pickupSkip = pickupSkipOf(task.waiting_on);
  // Waiting on: your turn (on it or an open sub-task, T-92), or someone else's.
  const turn = useTaskTurn(task);
  const contexts = hideContexts ? [] : task.contexts;
  const snoozed = isSnoozed(task.snoozed_until);
  const inlineSummary = showSummary && task.ai_summary ? taskSummaryText(task.ai_summary) : null;
  // Shown in the card, the hover icon would only repeat it.
  const summaryIcon = task.ai_summary && !inlineSummary;
  // On the Board, a run that's going shows along the card's top edge.
  const run = useTaskActiveRun(task.id);
  // SF-T-278: otherwise, the runs going on its sub-tasks, together, in the same place.
  const subtaskRuns = useSubtaskActiveRuns(task.id, task.subtask_titles.map((s) => s.task_id)).map(({ taskId, run: r }) => ({
    ref: formatRef(task.subtask_titles.find((s) => s.task_id === taskId)!.number),
    run: r,
  }));
  // SF-T-206: its newest run failed; a run going since (the Board's live runs) has replaced it.
  const failedRun = run ? null : task.failed_run;
  // SF-T-302: its run built it and waits for people's picks on its suggestions before the PR.
  const picksRun = run && run.pause === 'suggestions first' && run.live_parked_at && isRunActive(run.status) ? run : null;
  const hasFlags = Boolean(
    task.upstream?.unreachable || task.draft || failedRun || picksRun || pickupSkip || run?.limit_warning || snoozed || task.pending_patches || task.pending_approvals
  );

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      className={cn(
        'group relative cursor-pointer space-y-2 rounded-md border bg-card p-2.5 text-left text-sm shadow-xs transition-[box-shadow,border-color,background-color] hover:border-foreground/20 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        dragging && 'rotate-1 shadow-lg ring-2 ring-primary/30',
        task.archived && 'opacity-60',
        fadeWhenWaiting && turn.waitingOnOthers && !dragging && 'opacity-60 hover:opacity-100',
        drop.dragging && 'border-primary ring-2 ring-primary/40',
        selected !== undefined && 'pr-8',
        selected && 'border-primary ring-2 ring-primary/40',
        project && 'border-l-4',
        className
      )}
      style={project ? { ...style, borderLeftColor: project.color } : style}
      {...props}
      {...drop.bind}
    >
      {run ? <RunProgressBar run={run} variant="stripe" /> : subtaskRuns.length ? <SubtaskRunsProgressStripe runs={subtaskRuns} /> : null}
      {selected !== undefined ? (
        <span
          aria-hidden
          className={cn(
            'absolute right-2 top-2 flex size-4 items-center justify-center rounded-full border',
            selected ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/40 bg-background'
          )}
        >
          {selected ? <Check className="size-3" strokeWidth={3} /> : null}
        </span>
      ) : null}

      {/* SF-T-151: where it's mirrored from, priority and ref lead the title, as small marks, not chips. */}
      <p className="break-words leading-snug">
        {/* SF-T-207: the source it's on, by its logo, then (SF-T-176) who it's for. */}
        {task.upstream ? <UpstreamMark upstream={task.upstream} className="mr-1.5 align-[-0.15em]" /> : null}
        <TaskRequestor task={task} members={members} className="mr-1.5 -mt-0.5" />
        {priority ? <PriorityIcon priority={priority} className="mr-1 align-[-0.15em]" /> : null}
        {project ? (
          <span className="mr-1.5 inline-flex items-center gap-1 align-[-0.125em] text-xs tabular-nums text-muted-foreground" data-hint={project.name}>
            <ProjectIcon icon={project.icon} colour={project.color} className="size-3.5" />
            {task.number ? formatRef(task.number) : <span className="sr-only">{project.name}</span>}
          </span>
        ) : task.number ? (
          <span className="mr-1.5 text-xs tabular-nums text-muted-foreground">{formatRef(task.number)}</span>
        ) : null}
        {task.title}
      </p>
      {/* SF-T-176: shown for a sub-task the search matched: which. */}
      {matchedSubtasks?.length ? (
        <ul className="space-y-0.5 text-xs text-muted-foreground" data-testid="task-card-matched-subtasks">
          {matchedSubtasks.slice(0, 3).map((s) => (
            <li key={s.task_id} className="flex min-w-0 items-center gap-1">
              <ListChecks className="size-3.5 shrink-0" aria-hidden />
              <span className="shrink-0 tabular-nums">{formatRef(s.number)}</span>
              <span className="truncate">{s.title}</span>
            </li>
          ))}
          {matchedSubtasks.length > 3 ? <li className="pl-4.5">+{matchedSubtasks.length - 3} more sub-tasks</li> : null}
        </ul>
      ) : null}
      {inlineSummary ? <p className="break-words text-xs leading-snug text-muted-foreground">{inlineSummary}</p> : null}
      <TaskCardChips task={task} labels={labels} types={types} hideType={hideType} lead={false} />
      {/* Where it sits among other tasks, on one wrapping line. */}
      {task.stacked_on || task.parent ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {task.stacked_on ? <StackedOn stackedOn={task.stacked_on} variant="card" /> : null}
          {task.parent ? <SubtaskOf parent={task.parent} variant="card" /> : null}
          {/* SF-T-268: the sub-tasks of its parent it waits on. */}
          {task.parent && task.after?.length ? <SubtaskAfter after={task.after} /> : null}
        </div>
      ) : null}
      {/* What needs a look, on one wrapping line rather than a line each. */}
      {hasFlags ? (
        <div className="flex flex-wrap items-center gap-1">
          {/* SF-T-151: its source won't let the factory see it any more. */}
          <BrokenLinkBadge upstream={task.upstream} />
          {/* T-27: the AI builder made it on its own; it's never built until a person promotes it. */}
          {task.draft ? (
            <StatusBadge size="sm" tone="warning" data-hint="Made by the AI builder. Open it to promote or discard it; it isn't built until then.">
              Draft
            </StatusBadge>
          ) : null}
          {failedRun ? <FailedRunBadge run={failedRun} /> : null}
          {picksRun ? (
            <StatusBadge
              size="sm"
              meta={RUN_PICKS_META}
              data-hint={`R-${picksRun.number} built it and waits for your picks on its suggestions (or answers to its questions) before the PR. Open the task to pick, or press Wrap up.`}
            />
          ) : null}
          {/* T-65: why the AI builder hasn't picked it up yet. */}
          {pickupSkip ? (
            <PickupSkipBadge skip={pickupSkip} detail={subtaskRuns.length ? `Going now: ${subtaskRunsSentence(subtaskRuns)}.` : undefined} />
          ) : null}
          {/* T-28: its run went to a machine near its Claude weekly limit. */}
          {run?.limit_warning ? <RunLimitBadge warning={run.limit_warning} /> : null}
          {/* Only seen with "Include snoozed" on, or in a Tasks panel's Snoozed list. */}
          {snoozed ? (
            <Badge variant="info" shape="square" size="sm">
              <AlarmClock />
              Snoozed until {snoozeDay(task.snoozed_until!)}
            </Badge>
          ) : null}
          {/* T-57: the AI builder asks for a change it isn't allowed to make itself. */}
          {task.pending_patches ? <TaskCardPatchButton taskId={task.id} count={task.pending_patches} /> : null}
          {/* SF-T-177: its run's agent waits on a permission approval in T3 Code (the task's Approval request section). */}
          {task.pending_approvals ? (
            <Badge variant="warning" shape="square" size="sm" data-hint="The AI builder waits on a permission approval: open the task to see it, approve it in T3 Code or deny it">
              <ShieldAlert />
              Needs approval
            </Badge>
          ) : null}
        </div>
      ) : null}
      {contexts.length ? (
        <div className="flex flex-wrap gap-1">
          {contexts.slice(0, 2).map((c) => {
            const Icon = CONTEXT_ICONS[c.type];
            return (
              <Badge key={`${c.type}:${c.id}`} variant="muted" shape="square" size="sm" className="max-w-full">
                <Icon />
                <span className="truncate">{c.title}</span>
              </Badge>
            );
          })}
          {contexts.length > 2 ? (
            <Badge variant="muted" shape="square" size="sm">
              +{contexts.length - 2}
            </Badge>
          ) : null}
        </div>
      ) : null}
      {prs.length ? <TaskCardPrs taskId={task.id} prs={prs} parent={task.parent} hasSubtasks={!!task.subtasks} waiting={task.waiting_on} /> : null}
      {summaryIcon || hasTaskCardCounts(task) || task.tags.length || hasTaskCardPeople(task, turn) ? (
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {/* The AI summary (T-17) on hover; dragging hides it (HintLayer), and phones read it on the task. */}
          {summaryIcon && task.ai_summary ? (
            <span className="inline-flex items-center text-ai" data-hint={taskSummaryText(task.ai_summary)} data-testid="task-card-ai-summary">
              <Sparkles className="size-3.5" aria-hidden />
            </span>
          ) : null}
          <TaskCardCounts task={task} turn={turn} />
          {/* Tags share the counts' line and stop at "…", so they never add a line to the card. */}
          {task.tags.length ? (
            <span className="min-w-0 flex-1 truncate" data-hint={task.tags.map((t) => `#${t}`).join(' ')}>
              {task.tags.map((t) => `#${t}`).join(' ')}
            </span>
          ) : null}
          <TaskCardPeople task={task} members={members} turn={turn} formatRef={formatRef} className="ml-auto" />
        </div>
      ) : null}
    </div>
  );
});
